import styled from '@emotion/styled';
import { Box, Chip, Typography } from '@mui/material';
import { useState } from 'react';
import type { ApprovalRequest } from '../models';
import { colorAgentBorder, colorNeutralGray, colorSemanticWarning } from '../styles/tokens';

const SAFE_ARG_KEYS = new Set([
  'fiscal_year',
  'fiscal_month',
  'contract_number_from',
  'contract_number_to',
  'lease_company_code_from',
  'lease_company_code_to',
  'page',
  'rows_per_page',
]);

const CardContainer = styled(Box)`
  align-self: flex-start;
  max-width: 90%;
  border: 1px solid ${colorAgentBorder};
  border-left: 3px solid ${colorSemanticWarning};
  border-radius: 2px;
  padding: 6px 8px;
  font-size: 12px;
`;

const CardTitle = styled(Typography)`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const CardText = styled(Typography)`
  font-size: 12px;
  margin-bottom: 4px;
`;

const DetailToggle = styled(Typography)`
  font-size: 10px;
  color: text.secondary;
  cursor: pointer;
  text-decoration: underline;
  display: block;
  margin-bottom: 4px;
`.withComponent('span');

const Actions = styled(Box)`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const ToolDetail = styled(Box)`
  margin-bottom: 4px;
  padding: 4px 6px;
  background-color: ${colorNeutralGray};
  border-radius: 2px;
  font-size: 11px;
`;

const EditArea = styled(Box)`
  margin-top: 4px;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  font-size: 12px;
  font-family: 'メイリオ', Meiryo, sans-serif;
  border: 1px solid ${colorAgentBorder};
  border-radius: 2px;
  padding: 4px 6px;
  resize: vertical;
  box-sizing: border-box;
`;

const BosButton = styled.button`
  height: 24px;
  padding: 1px 12px;
  border: 0.8px solid #a3a3a3;
  border-radius: 10px;
  font-size: 12px;
  font-family: 'メイリオ', Meiryo, sans-serif;
  color: #000;
  background-color: #f0f0f0;
  background-image: url(/images/button.png);
  box-shadow: #888 1px 1px 2px 0px;
  cursor: pointer;
  line-height: normal;
  white-space: nowrap;

  &:hover {
    opacity: 0.88;
    background-image: url(/images/button_hover.png);
  }

  &:active {
    box-shadow: #888 0px 0px 1px 0px;
  }

  &:focus-visible {
    outline: 2px solid #002169;
    outline-offset: 1px;
  }

  &:disabled {
    background-image: url(/images/button_disable.png);
    background-color: rgba(239, 239, 239, 0.3);
    color: rgba(16, 16, 16, 0.3);
    border-color: #ccc;
    cursor: default;
    box-shadow: none;
  }
`;

const ArgsSummary = styled(Typography)`
  font-size: 12px;
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.6);
`;

interface ApprovalCardProps {
  approvalRequest: ApprovalRequest;
  onApprove: () => void;
  onReject: () => void;
  onEdit: (editMessage: string) => void;
  disabled?: boolean;
}

function SafeArgsSummary({ toolArgs }: { toolArgs: Record<string, unknown> }) {
  const entries = Object.entries(toolArgs).filter(([k]) => SAFE_ARG_KEYS.has(k));
  if (entries.length === 0) return null;
  return <ArgsSummary>{entries.map(([k, v]) => `${k}: ${v}`).join(', ')}</ArgsSummary>;
}

export function ApprovalCard({
  approvalRequest,
  onApprove,
  onReject,
  onEdit,
  disabled = false,
}: ApprovalCardProps) {
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState('');
  const [showDetail, setShowDetail] = useState(false);

  const handleEdit = () => {
    if (editText.trim()) {
      onEdit(editText.trim());
    }
  };

  return (
    <CardContainer>
      <CardTitle>承認が必要な操作があります</CardTitle>

      {approvalRequest.message && <CardText>{approvalRequest.message}</CardText>}

      {approvalRequest.toolCalls.map((tc) => (
        <Box key={tc.toolCallId}>
          {tc.riskSummary && <CardText>{tc.riskSummary}</CardText>}
          <SafeArgsSummary toolArgs={tc.toolArgs} />
          {showDetail && (
            <ToolDetail>
              <Chip label={tc.toolName} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
            </ToolDetail>
          )}
        </Box>
      ))}
      <DetailToggle onClick={() => setShowDetail(!showDetail)}>
        {showDetail ? '詳細を隠す' : '技術詳細を表示'}
      </DetailToggle>

      {!editMode ? (
        <Actions>
          <BosButton onClick={onApprove} disabled={disabled}>
            承認
          </BosButton>
          <BosButton onClick={onReject} disabled={disabled}>
            キャンセル
          </BosButton>
          <BosButton onClick={() => setEditMode(true)} disabled={disabled}>
            条件を修正
          </BosButton>
        </Actions>
      ) : (
        <EditArea>
          <EditTextarea
            placeholder="修正内容を入力..."
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={disabled}
            rows={2}
          />
          <Actions>
            <BosButton onClick={handleEdit} disabled={disabled || !editText.trim()}>
              送信
            </BosButton>
            <BosButton
              onClick={() => {
                setEditMode(false);
                setEditText('');
              }}
              disabled={disabled}
            >
              戻る
            </BosButton>
          </Actions>
        </EditArea>
      )}
    </CardContainer>
  );
}
